import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { MessageSquare, Loader2, Send, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const AVATAR_COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-violet-500', 'bg-cyan-500'];

const InitialAvatar = ({ name, size = 9 }) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const colorIdx = (name || '').charCodeAt(0) % AVATAR_COLORS.length || 0;
  return (
    <div className={`w-${size} h-${size} rounded-full ${AVATAR_COLORS[colorIdx]} text-white flex items-center justify-center font-semibold flex-shrink-0 border border-gray-200`} style={{ width: `${size * 4}px`, height: `${size * 4}px` }}>
      {initial}
    </div>
  );
};

const BlogComments = ({ slug, blogAuthorId }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [guestName, setGuestName] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/blogs/${slug}/comments`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() || (!user && !guestName.trim())) return;
    setPosting(true);
    try {
      const r = await fetch(`${API_URL}/api/blogs/${slug}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: text.trim(), author_name: user ? undefined : guestName.trim() }),
      });
      if (r.ok) {
        const newComment = await r.json();
        setComments((prev) => [...prev, newComment]);
        setText('');
      } else {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || 'Failed to post comment');
      }
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    const r = await fetch(`${API_URL}/api/comments/${commentId}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) setComments((prev) => prev.filter((c) => c.comment_id !== commentId));
  };

  const canDelete = (c) => user && (user.user_id === c.user_id || user.user_id === blogAuthorId || user.role === 'admin');

  return (
    <div className="mt-8 p-6 bg-white rounded-2xl border border-gray-200 shadow-lg" data-testid="blog-comments-section">
      <div className="flex items-center mb-5">
        <MessageSquare size={18} className="mr-2 text-gray-700" />
        <span className="text-gray-900 font-bold">Comments ({comments.length})</span>
      </div>

      <form onSubmit={handleSubmit} className="mb-6 flex gap-3">
        <InitialAvatar name={user?.name || guestName || 'Guest'} size={10} />
        <div className="flex-1 space-y-2">
          {!user && (
            <input
              type="text"
              data-testid="comment-guest-name-input"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Your name"
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          )}
          <textarea
            data-testid="comment-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder="Share your thoughts..."
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="comment-submit-btn"
              disabled={posting || !text.trim() || (!user && !guestName.trim())}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {posting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}Post
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-500">No comments yet. Be the first to share your thoughts!</p>
      ) : (
        <div className="space-y-4">
          {comments.map((c) => (
            <div key={c.comment_id} data-testid={`comment-${c.comment_id}`} className="flex gap-3">
              <InitialAvatar name={c.user_name} />
              <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900 text-sm">{c.user_name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{formatDate(c.created_at)}</span>
                    {canDelete(c) && (
                      <button data-testid={`comment-delete-${c.comment_id}`} onClick={() => handleDelete(c.comment_id)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.content}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogComments;
